import React from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {Form, Field} from 'formik';
import InputBox from '../../../components/formik/InputBox';
import config from '../../../utils/config/contactus';
import SelectBox from '../../../components/formik/SelectBox';
import TextareaBox from '../../../components/formik/TextareaBox';
import yup from '../../../utils/yup';
import frontendUtils from '../../../utils/frontendUtils';
import classnames from 'classnames';
import AttachFiles from './AttachFiles';
import EmailField from '../../../components/accountsetting/EmailField';

export default function RequestForm({values, isSubmitting}) {
  const {t} = useTranslation();
  const requireAllFields =
    ['Q06_FEATURES_ISSUES', 'Q08_SHOPIFY_RELATED'].indexOf(
      values.questionType
    ) !== -1;

  const validateWebsiteUrl = value => {
    let schema = {};
    if (requireAllFields) {
      schema.websiteUrl = yup
        .string()
        .matches(
          /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/i,
          t('p.c.addrequest.valid.url', 'Please enter a valid URL.')
        )
        .nullable()
        .required();
    } else {
      schema.websiteUrl = yup
        .string()
        .matches(
          /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/i,
          t('p.c.addrequest.valid.url', 'Please enter a valid URL.')
        )
        .nullable();
    }
    schema = yup.object().shape(schema);
    let validateData = {};
    validateData.websiteUrl = value !== '' ? value : undefined;

    return schema
      .validate(validateData)
      .then(() => undefined)
      .catch(err => err.errors[0]);
  };

  const validateFiles = value => {
    let schema = {};
    if (requireAllFields) {
      schema.files = yup
        .array()
        .min(
          1,
          t(
            'p.c.addrequest.attach.files.required',
            'Please attach at least one file.'
          )
        );
    } else {
      schema.files = yup.array();
    }
    schema = yup.object().shape(schema);
    let validateData = {};
    validateData.files = value !== '' ? value : undefined;

    return schema
      .validate(validateData)
      .then(() => undefined)
      .catch(err => err.errors[0]);
  };

  return (
    <Form>
      <div className="note note-info">
        <Trans i18nKey="p.c.addrequest.info">
          We're always happy to listen and discuss our product and services. If
          you have any questions or suggestions, please send us a message with
          the details.
        </Trans>
      </div>
      <div className="kt-margin-b-10 text-right">
        <span className="font-italic invalid-feedback d-block">
          *
          <Trans i18nKey="p.c.addrequest.required.fields">
            Required fields
          </Trans>
        </span>
      </div>
      <div className="row">
        <div className="col-6">
          <div className="form-group row">
            <label className="col-3 col-form-label" htmlFor="firstName">
              <Trans i18nKey="p.c.addrequest.first.name">First Name</Trans>
              <span className="required">*</span>
            </label>
            <div className="col-9">
              <Field name="firstName" id="firstName" component={InputBox} />
            </div>
          </div>
        </div>
        <div className="col-6">
          <div className="form-group row">
            <label className="col-3 col-form-label" htmlFor="lastName">
              <Trans i18nKey="p.c.addrequest.last.name">Last Name</Trans>
              <span className="required">*</span>
            </label>
            <div className="col-9">
              <Field name="lastName" id="lastName" component={InputBox} />
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-6">
          <div className="form-group row">
            <label className="col-3 col-form-label" htmlFor="email">
              <Trans i18nKey="p.c.addrequest.email">Email Address</Trans>
              <span className="required">*</span>
            </label>
            <div className="col-9">
              <Field
                name="email"
                id="email"
                component={EmailField}
                placeholder={t(
                  'p.c.addrequest.email.desc',
                  'Enter your business e-mail address'
                )}
                isVerifyFreeDomain={false}
              />
            </div>
          </div>
        </div>
        <div className="col-6">
          <div className="form-group row">
            <label className="col-3 col-form-label">
              <Trans i18nKey="p.c.addrequest.question.type">
                Question Type
              </Trans>
              <span className="required">*</span>
            </label>
            <div className="col-9">
              <Field
                name="questionType"
                options={config.getQuestionTypes()}
                component={SelectBox}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <div className="form-group">
            <label htmlFor="description">
              <Trans i18nKey="p.c.addrequest.description">
                How can we help you?
              </Trans>
              <span className="required">*</span>
            </label>
            <Field
              name="description"
              id="description"
              rows="6"
              placeholder={t(
                'p.c.addrequest.description.placeholder',
                'Please include detailed description of the issue or error message. If possible, please attach a screenshot.'
              )}
              component={TextareaBox}
            />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <div className="form-group">
            <label htmlFor="websiteUrl">
              <Trans i18nKey="p.c.addrequest.website.url">Website URL</Trans>
              {requireAllFields && <span className="required">*</span>}
            </label>
            {!requireAllFields && (
              <span className="font-italic text-muted">
                {' '}
                (<Trans i18nKey="p.c.addrequest.optional">optional</Trans>)
              </span>
            )}
            <Field
              name="websiteUrl"
              id="websiteUrl"
              placeholder={t(
                'p.c.addrequest.website.url.placeholder',
                'Your website URL to integrate virtual try on.'
              )}
              validate={validateWebsiteUrl}
              component={InputBox}
            />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <div className="form-group">
            <label>
              <Trans i18nKey="p.c.addrequest.attach.files">Attach Files</Trans>
              {requireAllFields && <span className="required">*</span>}
            </label>
            {!requireAllFields && (
              <span className="font-italic text-muted">
                {' '}
                (<Trans i18nKey="p.c.addrequest.optional">optional</Trans>)
              </span>
            )}
            <div className="text-muted kt-margin-b-10">
              <Trans i18nKey="p.c.addrequest.attach.files.desc">
                Maximum 3 attached Files, size limit 5MB. Required file format:
                .xml, .txt, .pdf, .jpg, .gif, .png, .zip, .rar
              </Trans>
            </div>
            <Field
              name="files"
              validate={validateFiles}
              component={AttachFiles}
            />
          </div>
        </div>
      </div>
      <div className="text-center">
        <div className="form-group">
          <Trans i18nKey="p.c.addrequest.agree.terms">
            By clicking "Submit", you agree with our{' '}
            <a
              href={frontendUtils.getPrivacyUrl('contact-support')}
              target="_blank"
              rel="noreferrer noopener"
              className="kt-link"
            >
              Privacy Policy
            </a>{' '}
            &{' '}
            <a
              href={frontendUtils.getTermsUrl('contact-support')}
              target="_blank"
              rel="noreferrer noopener"
              className="kt-link"
            >
              Terms of Use
            </a>
            .
          </Trans>
        </div>
        <button
          type="submit"
          disabled={isSubmitting}
          className={classnames({
            btn: true,
            'btn-danger': true,
            'kt-spinner': true,
            'kt-spinner-v2': true,
            'kt-spinner--center': true,
            'kt-spinner--sm': true,
            'kt-spinner--warning': isSubmitting,
          })}
        >
          <div style={{visibility: isSubmitting ? 'hidden' : 'visible'}}>
            <Trans i18nKey="p.c.addrequest.submit">Submit</Trans>
          </div>
        </button>
      </div>
    </Form>
  );
}
